import { CircularProgress, makeStyles, TextField } from '@material-ui/core'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Checkbox, PageTitle } from '../root/components/common'
import { Autocomplete } from '@material-ui/lab'

import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'
import { getLocationStatistics } from './api/locations-api'
import { enqueueSnackbar } from '../root/actions'
import { useQuery } from 'react-query'
import { REPORT_TYPES, useReportTypes } from '../root/hooks/useReportTypes'
import { OptionSwitcher } from '../root/components/common/option-switcher/option-switcher'
import { LOCATION_STATS_RANGES } from '../root/constants'
import { PointerIcon } from 'lucide-react'
import { StatisticsModal } from '../root/components/common/statistics/statistics-modal'

const useStyles = makeStyles(theme => ({
    wrapper: {
        maxWidth: 1200
    },
    chartWrapper: {},
    titleCheckboxes: {
        display: 'flex'
    },
    pageTitle: {
        justifyContent: 'space-between'
    }
}))

function LocationStatistics(props) {
    const dispatch = useDispatch()
    const classes = useStyles()
    const locationId = props.match.params.locationId
    const isNew = locationId === 'new'

    const { reportSeverityGroups } = useReportTypes()
    const { theme, locale } = useSelector(state => state.main)

    const [includeSubLocations, setIncludeSubLocations] = useState(false)
    const [predefinedTimeRange, setPredefinedTimeRange] = useState(
        LOCATION_STATS_RANGES.lastMonth
    )
    const [reportType1, setReportType1] = useState({
        label: locale.reports.reportTypes[REPORT_TYPES.catch],
        value: REPORT_TYPES.catch
    })
    const [reportType2, setReportType2] = useState({
        label: locale.reports.reportTypes[REPORT_TYPES.false_triggering],
        value: REPORT_TYPES.false_triggering
    })
    const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false)
    const [statisticsModalData, setStatisticsModalData] = useState(null)

    const reportTypeOptions1 = useMemo(() => {
        return Object.values(REPORT_TYPES)
            .filter(rt => rt !== reportType2.value)
            .map(rt => {
                let label = locale.reports.reportTypes[rt]
                if (Number(rt) === 3) {
                    label = `${label}, ${locale.reports.reportTypes['3-bait']}`
                }
                return {
                    label,
                    value: rt
                }
            })
    }, [reportType2, locale])

    const reportTypeOptions2 = useMemo(() => {
        return Object.values(REPORT_TYPES)
            .filter(rt => rt !== reportType1.value)
            .map(rt => {
                let label = locale.reports.reportTypes[rt]
                if (Number(rt) === 3) {
                    label = `${label}, ${locale.reports.reportTypes['3-bait']}`
                }
                return {
                    label,
                    value: rt
                }
            })
    }, [reportType1, locale])

    const { reportType1Color, reportType2Color } = useMemo(() => {
        if (!reportSeverityGroups) return {}

        let reportType1Color = theme.primary
        let reportType2Color = theme.primary
        Object.keys(reportSeverityGroups).forEach(key => {
            if (key === 'info') {
                if (reportSeverityGroups[key].includes(reportType1.value)) {
                    reportType1Color = theme.active
                }
                if (reportSeverityGroups[key].includes(reportType2.value)) {
                    reportType2Color = theme.active
                }
            }
            if (key === 'warning') {
                if (reportSeverityGroups[key].includes(reportType1.value)) {
                    reportType1Color = theme.warning
                }
                if (reportSeverityGroups[key].includes(reportType2.value)) {
                    reportType2Color = theme.warning
                }
            }
            if (key === 'critical') {
                if (reportSeverityGroups[key].includes(reportType1.value)) {
                    reportType1Color = theme.error
                }
                if (reportSeverityGroups[key].includes(reportType2.value)) {
                    reportType2Color = theme.error
                }
            }
        })

        return {
            reportType1Color,
            reportType2Color
        }
    }, [theme, reportSeverityGroups, reportType1.value, reportType2.value])

    const {
        data: locationStatisticsData1,
        isLoading: isLocationStatisticsLoading1
    } = useQuery(
        [
            'getLocationStatistics1',
            locationId,
            reportType1.value,
            includeSubLocations
        ],
        () =>
            getLocationStatistics(
                locationId,
                reportType1.value,
                includeSubLocations
            ),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: true,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const {
        data: locationStatisticsData2,
        isLoading: isLocationStatisticsLoading2
    } = useQuery(
        [
            'getLocationStatistics2',
            locationId,
            reportType2.value,
            includeSubLocations
        ],
        () =>
            getLocationStatistics(
                locationId,
                reportType2.value,
                includeSubLocations
            ),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: true,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const locationStatsData1 = useMemo(() => {
        if (!locationStatisticsData1) return []

        return predefinedTimeRange === LOCATION_STATS_RANGES.lastMonth
            ? locationStatisticsData1.month
            : locationStatisticsData1.week
    }, [predefinedTimeRange, locationStatisticsData1])

    const locationStatsData2 = useMemo(() => {
        if (!locationStatisticsData2) return []

        return predefinedTimeRange === LOCATION_STATS_RANGES.lastMonth
            ? locationStatisticsData2.month
            : locationStatisticsData2.week
    }, [predefinedTimeRange, locationStatisticsData2])

    const handleStatisticsModalOpen = useCallback(
        data => {
            if (!data.payload?.[data.dataKey]) return
            setIsStatisticsModalOpen(true)
            setStatisticsModalData(data)
        },
        [setIsStatisticsModalOpen, setStatisticsModalData]
    )

    const handleStatisticsModalClose = useCallback(() => {
        setIsStatisticsModalOpen(false)
        setStatisticsModalData(null)
    }, [setIsStatisticsModalOpen, setStatisticsModalData])

    return (
        <div className={classes.wrapper}>
            <PageTitle
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>{locale.locations.locationStatistics}</div>
                        <div
                            style={{
                                color: theme.textPrimaryReverted,
                                fontSize: 14,
                                fontWeight: 600,
                                marginLeft: 10,
                                backgroundColor: `${theme.active}94`,
                                padding: '2px 8px',
                                borderRadius: theme.borderRadius
                            }}
                        >
                            Beta
                        </div>
                    </div>
                }
                className={classes.pageTitle}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        label={locale.locations.includeSubLocations}
                        checked={includeSubLocations}
                        onClick={(_, checked) =>
                            setIncludeSubLocations(checked)
                        }
                    />
                    <OptionSwitcher
                        options={{
                            [LOCATION_STATS_RANGES.lastMonth]:
                                locale.locations.last30Days,
                            [LOCATION_STATS_RANGES.lastWeek]:
                                locale.locations.last7Days
                        }}
                        selected={predefinedTimeRange}
                        onChange={setPredefinedTimeRange}
                    />
                </div>
            </PageTitle>

            <div style={{ width: '100%' }}>
                <Autocomplete
                    disableClearable={true}
                    options={reportTypeOptions1}
                    style={{
                        width: 300,
                        marginLeft: 56,
                        marginTop: 30,
                        marginBottom: 20
                    }}
                    getOptionLabel={option => option.label}
                    renderInput={params => {
                        return (
                            <TextField
                                {...params}
                                label={locale.reports.event}
                                variant="outlined"
                                InputProps={{
                                    ...{
                                        style: { borderRadius: 7 }
                                    },
                                    ...params.InputProps
                                }}
                            />
                        )
                    }}
                    value={reportType1}
                    onChange={(event, newValue) => {
                        setReportType1(newValue)
                    }}
                    noOptionsText={locale.common.nothingWasFound}
                />
                <div
                    style={{
                        width: '100%',
                        height: 200,
                        position: 'relative'
                    }}
                >
                    {isLocationStatisticsLoading1 && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                        >
                            <CircularProgress size={20} color={theme.primary} />
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            width={500}
                            height={200}
                            data={locationStatsData1}
                            syncId="anyId"
                            margin={{
                                top: 10,
                                right: 30,
                                left: 0,
                                bottom: 0
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="report_date" />
                            <YAxis />
                            <Tooltip
                                content={
                                    <CustomTooltip
                                        locale={locale}
                                        reportType={reportType1.value}
                                    />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey={reportType1.value}
                                style={{ cursor: 'pointer' }}
                                stroke={reportType1Color}
                                fill={reportType1Color}
                                dot={{ strokeWidth: 6 }}
                                activeDot={{
                                    style: { cursor: 'pointer' },
                                    onClick: (e, data) =>
                                        handleStatisticsModalOpen(data)
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <Autocomplete
                    disableClearable={true}
                    options={reportTypeOptions2}
                    style={{
                        width: 300,
                        marginLeft: 56,
                        marginTop: 30,
                        marginBottom: 20
                    }}
                    getOptionLabel={option => option.label}
                    renderInput={params => {
                        return (
                            <TextField
                                {...params}
                                label={locale.reports.event}
                                variant="outlined"
                                InputProps={{
                                    ...{
                                        style: { borderRadius: 7 }
                                    },
                                    ...params.InputProps
                                }}
                            />
                        )
                    }}
                    value={reportType2}
                    onChange={(event, newValue) => {
                        setReportType2(newValue)
                    }}
                    noOptionsText={locale.common.nothingWasFound}
                />
                <div
                    style={{
                        width: '100%',
                        height: 200,
                        position: 'relative'
                    }}
                >
                    {isLocationStatisticsLoading2 && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                        >
                            <CircularProgress size={20} color={theme.primary} />
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            width={500}
                            height={200}
                            data={locationStatsData2}
                            syncId="anyId"
                            margin={{
                                top: 10,
                                right: 30,
                                left: 0,
                                bottom: 0
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="report_date" />
                            <YAxis />
                            <Tooltip
                                content={
                                    <CustomTooltip
                                        locale={locale}
                                        reportType={reportType2.value}
                                    />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey={reportType2.value}
                                style={{ cursor: 'pointer' }}
                                stroke={reportType2Color}
                                fill={reportType2Color}
                                dot={{ strokeWidth: 6 }}
                                activeDot={{
                                    style: { cursor: 'pointer' },
                                    onClick: (e, data) =>
                                        handleStatisticsModalOpen(data)
                                }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
            {isStatisticsModalOpen && statisticsModalData ? (
                <StatisticsModal
                    open={isStatisticsModalOpen}
                    onClose={handleStatisticsModalClose}
                    locationStatisticsData={statisticsModalData}
                />
            ) : null}
        </div>
    )
}

export default withRouter(LocationStatistics)

const CustomTooltip = ({ active, payload, label, locale, reportType }) => {
    const isVisible = active && payload && payload.length
    let devicesSortedLength = 0
    let devicesSortedToRender = null
    if (isVisible && payload[0].payload?.[`${reportType}_devices`].sorted) {
        devicesSortedLength =
            payload[0].payload?.[`${reportType}_devices`].sorted.length
        if (devicesSortedLength > 0) {
            devicesSortedToRender = payload[0].payload?.[
                `${reportType}_devices`
            ].sorted.slice(0, 3)
        }
    }
    const hasReports =
        isVisible && payload[0].payload?.[`${reportType}_reports`].length > 0
    return (
        <div
            style={{
                visibility: isVisible ? 'visible' : 'hidden',
                width: 300,
                margin: 0,
                border: '1px solid #f5f5f5',
                backgroundColor: '#fffc',
                padding: 10
            }}
        >
            {isVisible && (
                <>
                    <p className="label">{label}</p>
                    <p className="label">{`${locale.tom.amount} : ${payload[0].value}`}</p>
                    {devicesSortedToRender ? (
                        <div>
                            {devicesSortedToRender.map((device, idx) => (
                                <div key={device.serial_number}>
                                    {idx + 1}. {device.serial_number}:{' '}
                                    {device.amount}
                                </div>
                            ))}
                            {devicesSortedLength > 3 && <div>4. ...</div>}
                        </div>
                    ) : null}
                    {hasReports ? (
                        <p className="desc">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}
                            >
                                <PointerIcon size={30} />
                                {locale.locations.clickOnThePFM}
                            </div>
                        </p>
                    ) : null}
                </>
            )}
        </div>
    )
}
